import React, { useState, useRef, MutableRefObject } from 'react';
import { FlatList, Platform, View } from 'react-native';
import CompanyItem from './CompanyItem';
import Button from '@components/UI/Button/Button';
import buildDataSet from '@utils/dataUtils/buildDataSet';
import { useDispatch } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import { HIDE_ALL_MODALS, SHOW_AUTH_MODAL, SHOW_LOADING_MODAL } from '@reduxLocal/actions/types';
import { XStack, Text, YStack, H4, SizableText } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { MaterialCommunityIcons, MaterialIcons } from '@expo/vector-icons';
import DataModelSelectionModal, { DataModelSelectionModalHandle } from '@components/modals/DataModelSelectionModal/DataModelSelectionModal';
import apis from '@api/api';

const CompanyList = ({ searchResults, mapCallback }) => {
  const [selectAll, setSelectAll] = useState(false);
  const [refreshFlag, setRefreshFlag] = useState(false);
  const selectedList = useRef<MutableRefObject<any[]> | null>({ current: [] });
  const dataModelModalRef = useRef<DataModelSelectionModalHandle>(null);
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const theme = themeSelector();

  // Theme colors
  const colors = {
    background: theme ? '#ffffff' : '#374151',
    cardBackground: theme ? '#f8fafc' : '#4b5563',
    text: theme ? '#1f2937' : '#f9fafb',
    textSecondary: theme ? '#6b7280' : '#9ca3af',
    border: theme ? '#e5e7eb' : '#4b5563',
    accent: '#3b82f6',
    smartBlue: 'rgb(90,192,234)',
  };


  const _handleSelect = (item) => {
    if (selectedList.current && selectedList.current.current.includes(item)) {
      selectedList.current.current = (selectedList.current.current.filter(i => i !== item));
    } else if (selectedList.current) {
      selectedList.current.current = ([...selectedList.current.current, item]);
    }
    setRefreshFlag((prev) => !prev);
  }

  const _handleBuildDataSet = async () => {
    if (!selectedList.current?.current || selectedList.current.current.length === 0) {
      dispatch({ type: SHOW_AUTH_MODAL, data: { content: "No companies selected", type: "error" } });
      return;
    }

    // Open the data model selection modal
    dataModelModalRef.current?.openModal(selectedList.current.current);
  };

  const handleDataModelConfirm = async (
    selectedPOIs: any[],
    dataModelId: string | null,
    createNew: boolean,
    modelName?: string
  ) => {
    try {
      dispatch({ type: SHOW_LOADING_MODAL, payload: 'Adding businesses to data model...' });

      const result = await apis.addPOIsToDataModel(
        selectedPOIs,
        dataModelId,
        createNew,
        modelName,
        ['networking', 'leads', 'import']
      );

      dispatch({ type: HIDE_ALL_MODALS });
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: `Successfully added ${selectedPOIs.length} businesses! Enrichment is in progress.`,
          type: "success"
        }
      });

      // Clear selection
      selectedList.current.current = [];
      setRefreshFlag(prev => !prev);

      // Navigate to the data screen
      setTimeout(() => {
        (navigation as any).navigate("Data", {
          screen: "Capture",
          params: { dataModelId: result.dataModelId }
        });
      }, 1500);
    } catch (error) {
      dispatch({ type: HIDE_ALL_MODALS });
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: {
          content: "Failed to add businesses. Please try again.",
          type: "error"
        } 
      });
      console.error('Error adding POIs:', error);
    }
  };

  return (
    <YStack
      flex={1}
      backgroundColor={Platform.OS === 'web' ? 'transparent' : colors.background}
      style={{
        ...(Platform.OS === 'web' && {
          backdropFilter: 'blur(10px)',
          WebkitBackdropFilter: 'blur(10px)',
        } as any),
      }}
    >
      <FlatList
        extraData={refreshFlag}
        stickyHeaderIndices={[0]}
        ListEmptyComponent={() => (
          <YStack padding="$6" gap="$4" alignItems="center" justifyContent="center" flex={1} minHeight={300}>
            <MaterialCommunityIcons
              name="office-building-outline"
              size={64}
              color={colors.textSecondary}
            />
            <H4 color={colors.text} textAlign="center">
              No Companies Found
            </H4>
            <SizableText
              color={colors.textSecondary}
              textAlign="center"
              maxWidth={280}
            >
              Try searching for keywords and companies above to discover networking opportunities
            </SizableText>
          </YStack>
        )}
        ListHeaderComponent={() => (
          <XStack
            marginBottom={20}
            gap={12}
            paddingHorizontal={5}
            paddingVertical={8}
            backgroundColor={Platform.OS === 'web' ? 'rgba(255, 255, 255, 0.1)' : colors.background}
            style={{
              maxHeight: 80,
              alignSelf: "stretch",
              borderRadius: 16, // rounded-2xl equivalent
              shadowColor: '#000',
              shadowOffset: { width: 0, height: 4 },
              shadowOpacity: 0.3,
              shadowRadius: 8,
              elevation: 8,
              // Glass morphism effect for web
              ...(Platform.OS === 'web' && {
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
              } as any),
            }}
          >
            <Button
              customStyle={{
                borderRadius: 12,
                flex: 1,
                backgroundColor: colors.smartBlue,
                borderWidth: 1,
                borderColor: colors.smartBlue,
                minHeight: 48,
                shadowRadius: 0,
                shadowOpacity: 0,
              }}
              textColor='white'
              icon={<MaterialIcons name="select-all" size={20} color="white" />}
              iconColor='white'
              buttonText={selectedList.current?.current?.length === searchResults?.length ? "Deselect All" : "Select All"}
              onPress={() => {
                if (selectedList.current?.current?.length === searchResults?.length) {
                  selectedList.current.current = [];
                  setSelectAll(false);
                } else {
                  selectedList.current.current = searchResults || [];
                  setRefreshFlag((prev) => !prev);
                }
              }}
            />
            <Button
              disabled={selectedList.current?.current?.length < 1}
              customStyle={{
                borderRadius: 12,
                flex: 1,
                backgroundColor: selectedList.current?.current?.length < 1 ? colors.cardBackground : colors.accent,
                borderWidth: 1,
                borderColor: selectedList.current?.current?.length < 1 ? colors.border : colors.accent,
                minHeight: 48,
                shadowRadius: 0,
                shadowOpacity: 0,
              }}
              textColor={selectedList.current?.current?.length < 1 ? colors.textSecondary : 'white'}
              icon="plus"
              iconColor={selectedList.current?.current?.length < 1 ? colors.textSecondary : 'white'}
              buttonText={`Add ${selectedList.current?.current?.length || 0} Companies`}
              onPress={() => {
                _handleBuildDataSet();
              }}
            />
          </XStack>
        )}
        data={searchResults}
        contentContainerStyle={{
          paddingHorizontal: 16,
          paddingBottom: 20,
          backgroundColor: Platform.OS === 'web' ? 'transparent' : colors.background,
        }}
        keyExtractor={(item, index) => item?._id || item?.id || String(index)}
        renderItem={({ item, index }) => {
          return (
            <CompanyItem
              selectCallback={_handleSelect}
              isSelected={selectedList.current?.current?.includes(item)}
              selectAll={refreshFlag}
              mapCallback={mapCallback}
              item={item}
              index={index}
            />
          )
        }}
        removeClippedSubviews={true}
        maxToRenderPerBatch={10}
        windowSize={10}
        initialNumToRender={8}
        updateCellsBatchingPeriod={100}
        showsVerticalScrollIndicator={false}
      />

      {/* Data Model Selection Modal */}
      <DataModelSelectionModal
        ref={dataModelModalRef}
        onConfirm={handleDataModelConfirm}
      />
    </YStack>
  )
}

export default CompanyList;